import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Instagram } from "lucide-react";
import { toast } from "sonner";
import { scrollToSection } from "./scroll";
import { useSiteData, useSectionDesign } from "../../context/SiteData";

const LINKS = [
  { label: "Home", hash: "#home" },
  { label: "Tours", hash: "#tours" },
  { label: "Services", hash: "#services" },
  { label: "Destinations", hash: "#destinations" },
  { label: "About", hash: "#about" },
  { label: "Contact", hash: "#contact" },
];

export const Footer = () => {
  const { content } = useSiteData();
  const sec = useSectionDesign("footer");
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const go = (hash) => {
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(hash), 150);
    } else {
      scrollToSection(hash);
    }
  };

  const subscribe = (e) => {
    e.preventDefault();
    if (!/.+@.+\..+/.test(email)) {
      toast.error("Please enter a valid email to subscribe.");
      return;
    }
    toast.success("You're on the list — welcome aboard.");
    setEmail("");
  };

  return (
    <footer className="bg-[#0b132b] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <RouterLink to="/" data-testid="footer-logo-home-link" aria-label="Voyage Switzerland — homepage">
            <span className="inline-flex bg-white rounded-2xl px-3 py-2">
              <img src="/logo.png" alt="Voyage Switzerland" className="h-12 w-auto" />
            </span>
          </RouterLink>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs">
            {sec.text("tagline", content.footer_tagline)}
          </p>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(content.contact_address || "")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-address-link"
            className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <MapPin className="w-4 h-4 text-[var(--brand)] shrink-0" />
            {content.contact_address}
          </a>
          <a
            href="https://www.instagram.com/voyageswisscom/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="mt-3 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <Instagram className="w-4 h-4 text-[var(--brand)] shrink-0" />
            @voyageswisscom
          </a>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-widest text-slate-400">Explore</h4>
          <ul className="mt-5 space-y-3">
            <li>
              <RouterLink
                to="/"
                data-testid="footer-home-link"
                className="text-sm font-semibold text-slate-300 hover:text-white transition-colors"
              >
                Private &amp; Luxury Switzerland Tours
              </RouterLink>
            </li>
            {LINKS.map((l) => (
              <li key={l.hash}>
                <button
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.hash)}
                  className="text-sm font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-widest text-slate-400">Popular</h4>
          <ul className="mt-5 space-y-3">
            {[
              ["Private Tours in Switzerland", "/private-tours-switzerland"],
              ["Luxury Switzerland Tours", "/luxury-tours-switzerland"],
              ["Zurich Private Tours", "/zurich-private-tours"],
              ["Swiss Alps Tours", "/swiss-alps-tours"],
              ["Chauffeur Service", "/switzerland-chauffeur-service"],
              ["Zurich Airport Transfer", "/zurich-airport-transfer"],
              ["Travel Guide", "/blog"],
            ].map(([label, to]) => (
              <li key={to}>
                <RouterLink
                  to={to}
                  data-testid={`footer-popular-${to.slice(1)}`}
                  className="text-sm font-semibold text-slate-300 hover:text-white transition-colors"
                >
                  {label}
                </RouterLink>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-sm uppercase tracking-widest text-slate-400">Stay in the loop</h4>
          <p className="mt-5 text-sm text-slate-400">{content.footer_newsletter}</p>
          <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="footer-newsletter-form">
            <input
              data-testid="footer-newsletter-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="flex-1 min-w-0 rounded-full bg-white/10 border border-white/15 px-5 py-3 text-sm font-medium placeholder:text-slate-500 outline-none focus:border-[var(--brand)] transition"
            />
            <button
              data-testid="footer-newsletter-button"
              type="submit"
              className="w-12 h-12 shrink-0 rounded-full bg-[var(--brand)] flex items-center justify-center transition-colors hover:bg-[var(--brand-dark)]"
              aria-label="Subscribe"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs font-semibold text-slate-500" data-testid="footer-copyright">
            © {new Date().getFullYear()} Voyage Switzerland. All rights reserved.
          </p>
          <p className="text-xs font-semibold text-slate-500">Switzerland, Your Way.</p>
        </div>
      </div>
    </footer>
  );
};
